import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import React, { useEffect, useState } from "react";
import PageHeading from "../components/PageHeading/PageHeading";
import Notice from "../layouts/Notice/Notice";
import { useMenu } from "../contexts/MenuContext";
import { urlToArr, useCurrentPageObject } from "../helpers/Helpers"; // Import utility function

const withForm = (OriginalComponent, sectionPath = null) => {
	function NewComponent() {
		const { settingData, setSettingData, menuData } = useMenu();
		const [count, setCount] = useState(0);
		const [saveLoading, setSaveLoading] = useState(false);
		const [showFormNotice, setShowFormNotice] = useState(false);
		const [activeDiscard, setActiveDiscard] = useState(false);
		const [processing, setProcessing] = useState(false);
		const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
		const [databaseData, setDatabaseData] = useState(null);
		const [fieldValidationStatus, setFieldValidationStatus] = useState({});
		const OPTIONS_API_URL = "ultimate-security/v1/options";

		// Store the database data when component mounts and when settingData changes
		useEffect(() => {
			const initializeDatabaseData = async () => {
				// For the first load, fetch fresh data directly from the database
				const freshData = await fetchDatabaseData();

				if (freshData) {
					// Store the database data for comparison
					if (sectionPath) {
						// For sections, store just the relevant section
						setDatabaseData(freshData);
					} else {
						// For top-level, store all
						setDatabaseData(freshData);
					}
				}
			};

			initializeDatabaseData();
		}, []); // Only run on mount

		// Continuously monitor for changes between current settingData and database data
		useEffect(() => {
			if (settingData && databaseData) {
				let currentData;

				if (sectionPath) {
					// Get the specific section we're working with
					currentData = getNestedValue(settingData, sectionPath);
				} else {
					// For top-level components, use all data
					currentData = settingData;
				}
				// Check if current data differs from database data
				const hasChanges = checkForChanges(currentData, databaseData);
				// Update state based on comparison
				setHasUnsavedChanges(hasChanges);
				// Update localStorage based on whether there are changes
				if (hasChanges) {
					if (sectionPath) {
						localStorage.setItem(
							`changes_${sectionPath}`,
							JSON.stringify({
								changed: true,
								timestamp: Date.now(),
							}),
						);
					} else {
						localStorage.setItem(
							`changes_all`,
							JSON.stringify({
								changed: true,
								timestamp: Date.now(),
							}),
						);
					}
				} else {
					// Remove from localStorage if values match database
					if (sectionPath) {
						localStorage.removeItem(`changes_${sectionPath}`);
					} else {
						localStorage.removeItem(`changes_all`);
					}
				}
			}
		}, [settingData, databaseData, sectionPath]);

		// Helper to get nested value using path string
		const getNestedValue = (obj, path) => {
			if (!path || !obj) return obj;

			const pathParts = path.split(".");
			let result = obj;

			for (const part of pathParts) {
				if (
					result === undefined ||
					result === null ||
					typeof result !== "object"
				) {
					return undefined;
				}
				result = result[part];
			}

			return result;
		};
		// Function to check if current data matches database data
		const checkForChanges = (current, original) => {
			// If either is null, we can't compare
			if (!current || !original) return false;

			// Deep compare objects
			return JSON.stringify(current) !== JSON.stringify(original);
		};
		// Fetch fresh database data
		const fetchDatabaseData = async () => {
			try {
				let endpoint = OPTIONS_API_URL;
				if (sectionPath) {
					endpoint += `?section=${sectionPath}`;
				}

				const response = await apiFetch({
					path: endpoint,
					method: "GET",
				});

				return response;
			} catch (error) {
				console.error(`Error fetching settings:`, error);
				return null;
			}
		};
		// Updated handleChange function in your withForm HOC
		const handleChange = (fieldPath, value, isValid = true) => {
			if (!fieldPath) {
				console.error("Field path is required");
				return;
			}
			setFieldValidationStatus((prev) => ({
				...prev,
				[fieldPath]: isValid,
			}));
			setSettingData((prev) => {
				const newState = JSON.parse(JSON.stringify(prev));

				let parts;
				if (fieldPath.includes("::")) {
					parts = fieldPath.split("::");

					// Decode plugin file name only
					if (parts[2] === "plugins_functionalities" && parts[3]) {
						parts[3] = decodeURIComponent(parts[3]);
					}
				} else {
					parts = fieldPath.split(".");
				}
				let current = newState;
				for (let i = 0; i < parts.length - 1; i++) {
					if (!current[parts[i]]) {
						current[parts[i]] = {};
					}
					current = current[parts[i]];
				}

				current[parts[parts.length - 1]] = value;

				return newState;
			});

			setCount((prevCount) => prevCount + 1);
		};
		// Enhanced handleSave function
		const handleSave = () => {
			// Check if any fields have validation errors
			const hasValidationErrors = Object.values(
				fieldValidationStatus,
			).some((isValid) => !isValid);

			if (hasValidationErrors) {
				console.error("Some fields have validation errors");
				window.scrollTo(0, 0);
				alert("Please fix validation errors before saving.");
				return;
			}

			// Continue with the rest of your save logic...
			setProcessing(true);
			setSaveLoading(true);

			// Create the data structure to save
			const dataToSave = { ultimate_security_options: {} };

			if (sectionPath) {
				// For nested sections, get the specific section data
				const sectionData = getNestedValue(settingData, sectionPath);
				// Build the nested structure for the API
				const pathParts = sectionPath.split(".");
				let current = dataToSave["ultimate_security_options"];

				for (let i = 0; i < pathParts.length - 1; i++) {
					current[pathParts[i]] = {};
					current = current[pathParts[i]];
				}
				// Set the actual data at the final nesting level
				current[pathParts[pathParts.length - 1]] = sectionData;
			} else {
				// For top-level sections, use entire settingData
				dataToSave["ultimate_security_options"] = settingData;
				console.log("Full data to save:", settingData);
			}

			apiFetch({
				path: OPTIONS_API_URL,
				method: "POST",
				data: dataToSave,
			})
				.then((response) => {
					window.scrollTo(0, 0);

					setSaveLoading(false);
					setProcessing(false);
					setActiveDiscard(false);
					setShowFormNotice(true);
					setHasUnsavedChanges(false);

					// Clear changes from localStorage
					if (sectionPath) {
						localStorage.removeItem(`changes_${sectionPath}`);
					} else {
						localStorage.removeItem(`changes_all`);
					}
					// Update the database data to match the new saved state
					if (sectionPath) {
						const newSectionData = getNestedValue(
							settingData,
							sectionPath,
						);
						setDatabaseData(
							JSON.parse(JSON.stringify(newSectionData)),
						);
					} else {
						setDatabaseData(
							JSON.parse(JSON.stringify(settingData)),
						);
					}
					setTimeout(() => {
						setShowFormNotice(false);
					}, 3000);
				})
				.catch((error) => {
					console.error("Error saving settings:", error);
					setSaveLoading(false);
					setProcessing(false);
				});
		};
		const handleDiscard = async () => {
			setActiveDiscard(true);
			try {
				// Fetch the current settings from the server
				const freshData = await fetchDatabaseData();

				if (freshData) {
					setSettingData((prev) => {
						const newState = JSON.parse(JSON.stringify(prev));

						if (sectionPath) {
							const pathParts = sectionPath.split(".");
							let current = newState;

							for (let i = 0; i < pathParts.length - 1; i++) {
								if (!current[pathParts[i]])
									current[pathParts[i]] = {};
								current = current[pathParts[i]];
							}

							const lastPart = pathParts[pathParts.length - 1];
							current[lastPart] = freshData; // because freshData *is* the section
						} else {
							return freshData; // full reset
						}

						return newState;
					});

					// Clear localStorage
					if (sectionPath) {
						localStorage.removeItem(`changes_${sectionPath}`);
					} else {
						localStorage.removeItem("changes_all");
					}

					// No need to update databaseData as it's already the database state
					setHasUnsavedChanges(false);
				}
			} catch (error) {
				console.error("Error discarding changes:", error);
			} finally {
				setActiveDiscard(false);
			}
		};
		const urlArr = urlToArr();
		const pageObject = useCurrentPageObject(urlArr, menuData);

		return (
			<>
				{showFormNotice && <Notice />}
				<div className="settings-container">
					<div className="form-container bg-white border">
						<div className="form-header p-lg border-bottom">
							<PageHeading
								title={pageObject?.title || ""}
								subtitle={pageObject?.description || ""}
							/>
						</div>
						<div className="form-body p-lg border-bottom">
							<OriginalComponent handleChange={handleChange} />
						</div>
						<div className="form-footer p-lg">
							<div className="button-wrapper d-flex align-items-center gap-3">
								{sectionPath ? (
									<>
										<button
											type="button"
											className="button button-primary"
											onClick={handleSave}
											disabled={processing || activeDiscard || !hasUnsavedChanges}
										>
											{saveLoading
												? __("Saving...", "ultimate-security")
												: __("Save Changes", "ultimate-security")}
										</button>
										<button
											type="button"
											className="button button-secondary"
											onClick={handleDiscard}
											disabled={processing || activeDiscard || !hasUnsavedChanges}
										>
											{__("Discard Changes", "ultimate-security")}
										</button>
										{hasUnsavedChanges && (
											<div className="unsaved-changes-indicator">
												<span className="text-warning">
													<i className="dashicons dashicons-warning"></i>{" "}
													{__("Unsaved changes", "ultimate-security")}
												</span>
											</div>
										)}
									</>
								) : (
									<>
										<button className="button button-primary" disabled>
											{__("Save Change", "ultimate-security")}
										</button>
										<button className="button button-secondary" disabled>
											{__("Reset to Default", "ultimate-security")}
										</button>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}
	return NewComponent;
};
export default withForm;